import { fabric } from 'fabric';
import type { MutableRefObject } from 'react';
import Vec2 from 'vec2';

import {
  CANVAS_OBJECT_TYPE_PREFIX,
  CANVAS_OBJECT_TYPE_SUFFIX,
} from '~/core/constants';
import { DrawingAppliance, DrawingShape } from '~/core/models';

/**
 * Parameters for setting up appliance interaction refs.
 */
export interface ApplianceInteractionSetupParams {
  isShapeLogicRef: MutableRefObject<boolean>;
  movingItemRef: MutableRefObject<DrawingShape | string | undefined>;
  shapeStartDragRef: MutableRefObject<Vec2>;
  applianceId: string;
  pointer: Vec2;
}

/**
 * Parameters for moving previous appliance back to its group.
 */
export interface MovePreviousApplianceParams {
  canvas: fabric.Canvas;
  activeDrawingApplianceId: string | undefined;
  newApplianceId: string;
  drawingShapes: DrawingShape[];
}

/**
 * Result of finding an appliance in the drawingAppliances array.
 */
export interface ApplianceSearchResult {
  appliance: DrawingAppliance | undefined;
  displayName: string;
}

/**
 * Checks if a canvas object name represents an appliance.
 */
export const isApplianceName = (name: string | undefined): boolean =>
  Boolean(name?.includes(CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE));

/**
 * Extracts the appliance ID from a canvas object name.
 */
export const extractApplianceIdFromName = (name: string): string =>
  name.replace(`${CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE}_`, '');

/**
 * Finds an appliance in drawingAppliances by its canvas object name.
 */
export const findApplianceByCanvasName = (
  canvasName: string,
  drawingAppliances: DrawingAppliance[]
): ApplianceSearchResult => {
  const applianceId = extractApplianceIdFromName(canvasName);
  const appliance = drawingAppliances.find((appl) => appl.id === applianceId);

  const displayName = appliance
    ? (appliance.applianceModel.consumerDesc ?? appliance.applianceModel.name)
    : '';

  return { appliance, displayName };
};

/**
 * Finds a shape by matching targetName against canvasObjectName + SHAPE_GROUP suffix.
 */
export const findShapeByGroupTargetName = (
  shapes: DrawingShape[],
  targetName: string | undefined
): DrawingShape | undefined =>
  shapes.find(
    (dShape) =>
      targetName ===
      dShape.canvasObjectName + CANVAS_OBJECT_TYPE_SUFFIX.SHAPE_GROUP
  );

/**
 * Sets up refs for appliance interaction/movement.
 */
export const setupApplianceInteractionRefs = ({
  isShapeLogicRef,
  movingItemRef,
  shapeStartDragRef,
  applianceId,
  pointer,
}: ApplianceInteractionSetupParams): void => {
  isShapeLogicRef.current = false;
  movingItemRef.current = applianceId;
  shapeStartDragRef.current = pointer;
};

/**
 * Moves the previously selected appliance back to its fabricGroup.
 *
 * CRITICAL: Uses addWithUpdate() which differs from handleApplianceSelection.
 * This is intentional - see plan documentation for explanation.
 */
export const movePreviousApplianceBackToGroup = ({
  canvas,
  activeDrawingApplianceId,
  newApplianceId,
  drawingShapes,
}: MovePreviousApplianceParams): boolean => {
  if (
    !activeDrawingApplianceId ||
    activeDrawingApplianceId === newApplianceId
  ) {
    return false;
  }

  const previousApplianceObject = canvas
    .getObjects()
    .find(
      (obj) => obj.name === activeDrawingApplianceId && obj.type === 'image'
    ) as fabric.Image | undefined;

  if (!previousApplianceObject) {
    return false;
  }

  const previousShape = drawingShapes.find((dShape) =>
    dShape.appliances.some(
      (appliance) =>
        `${CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE}_${appliance.id}` ===
        activeDrawingApplianceId
    )
  );

  if (!previousShape?.fabricGroup) {
    return false;
  }

  canvas.remove(previousApplianceObject);
  previousShape.fabricGroup.addWithUpdate(previousApplianceObject);

  return true;
};

/**
 * Locks shape movement to prevent group drag during appliance extraction.
 */
export const lockShapeMovement = (target: fabric.Object): void => {
  target.lockMovementX = true;
  target.lockMovementY = true;
};

/**
 * Extracts an appliance from its fabricGroup to canvas root and selects it.
 */
export const extractApplianceFromGroupToCanvas = (
  canvas: fabric.Canvas,
  shape: DrawingShape,
  groupMemberClicked: fabric.Object
): void => {
  shape.fabricGroup.remove(groupMemberClicked);
  canvas.add(groupMemberClicked as fabric.Image);
  canvas.setActiveObject(groupMemberClicked as fabric.Image);
};

/**
 * Selects an appliance that's already on canvas root.
 */
export const selectCanvasRootAppliance = (
  canvas: fabric.Canvas,
  target: fabric.Object
): void => {
  canvas.setActiveObject(target as fabric.Image);
  canvas.renderAll();
};
